import { useEffect, useRef, useState, useMemo } from "react";
import {
  Modal, Box, Typography, FormControl, TextField,
  IconButton, Autocomplete, Button, CircularProgress, Tooltip,
  Fade, Alert
} from "@mui/material";
import { useSelector } from "react-redux";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GradingIcon from '@mui/icons-material/Grading';
import UndoIcon from '@mui/icons-material/Undo';
import moment from "moment";

import { updateContract, updateContractResult } from "../../services/backend/ContractService";
import usePaymentCutoff from "./usePaymentCutoff";
import {
  getContractField, getActivityStatus, getSubmitForReviewBlockReason, isMonthInCorrection,
  isActivityAddedInCycle, hasAddedActivitiesWithoutDescription, getPreviousExecutableIdx,
  getPeriodDateInfo
} from "../../functions";
import { CORRECTION_STATUS, PERMISSIONS, REVIEW_STATUS } from "../../constants";
import ChargeAccountActivity from "./ChargeAccountActivity";
import MySnackbar from "../generic/MySnackbar";
import DialogSignConfirmation from "../generic/DialogSignConfirmation";
import DialogConfirmation from "../generic/DialogConfirmation";

const modalStyle = {
  bgcolor: "background.paper",
  boxShadow: 24,
  left: "50%",
  maxWidth: "980px",
  maxHeight: "90vh",
  overflowY: "auto",
  p: 4,
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%"
};

const ChargeAccountModal = ({
  contract, payment, open, setContract, onClose
}) => {
  const user = useSelector((state) => state.user.value);
  const [activities, setActivities] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const [activity, setActivity] = useState('');
  const [approved, setApproved] = useState();
  const [loading, setLoading] = useState(false);
  const [infoSnack, setInfoSnack] = useState({ open: false });
  const [confirmInfo, setConfirmInfo] = useState({ open: false });
  const [returnInfo, setReturnInfo] = useState({ open: false });
  const [returnNote, setReturnNote] = useState('');
  const [removeInfo, setRemoveInfo] = useState({ open: false });
  // Marca de sesión: el contratista eliminó una actividad durante el ciclo de
  // corrección. El borrado es duro (la actividad sale del arreglo sin dejar
  // rastro), así que `requiresReReview` no puede deducirlo del contenido.
  const [removedInCycle, setRemovedInCycle] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const modalRef = useRef(null);

  const updateScrollHint = () => {
    const el = modalRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setShowScrollHint(hasOverflow && !atBottom);
  };

  const tasks = getContractField(contract.news || [], contract)('tasks');

  const isContractor = useMemo(() => contract?.contractor?.userId === user.userId, [contract, user]);

  const supportIDs = contract.supportIDs || [];
  // Apoyo a la Supervisión asignado a este contrato: marca revisiones y
  // devuelve actividades; nunca aprueba.
  const isSupportReviewer = user.permissions.includes(PERMISSIONS.SUPPORT_SUPERVISION.REVIEW)
    && supportIDs.includes(user.userId);
  const canReturnAsSupport = user.permissions.includes(PERMISSIONS.SUPPORT_SUPERVISION.RETURN)
    && supportIDs.includes(user.userId);

  // Mes "En corrección": desde la notificación agrupada de devoluciones hasta
  // que el contratista notifica su corrección. Mientras dure, las acciones de
  // revisión (devolver, deshacer, marcar revisada, notificar) quedan bloqueadas.
  const monthPayment = payment ? contract.payments?.[payment] : null;
  const inCorrection = isMonthInCorrection(monthPayment);

  // El contratista agrega obligaciones en borrador y, además, mientras el mes
  // esté "En corrección": la revisión está en pausa y la pelota es suya. Las
  // agregadas en el ciclo nacen Pendiente con `addedAt` (isActivityAddedInCycle)
  // y son editables/eliminables solo hasta que notifique la corrección. Nunca
  // se toca `approved`: el mes no vuelve a borrador.
  const canAddActivities = isContractor && (approved === undefined || inCorrection);

  // Puede devolver actividades: Supervisor EFECTIVO del contrato (considerando
  // novedades de cambio de supervisor), Apoyo a la Supervisión asignado o
  // ADMIN, mientras la cuenta de cobro esté enviada y sin aprobación definitiva.
  const canReturn = useMemo(() => {
    if (isContractor || approved !== false) return false;
    if (user.roles.includes('ADMIN')) return true;
    if (canReturnAsSupport) return true;
    const effectiveSupervisors = getContractField(contract.news || [], contract)('supervisors') || [];
    return user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.REJECT_ACTIVITY)
      && effectiveSupervisors.some((s) => s?.userId === user.userId);
  }, [isContractor, approved, user, contract, canReturnAsSupport]);

  // Supervisor EFECTIVO del contrato (considerando novedades).
  const isEffectiveSupervisor = useMemo(() => {
    const effectiveSupervisors = getContractField(contract.news || [], contract)('supervisors') || [];
    return effectiveSupervisors.some((s) => s?.userId === user.userId);
  }, [contract, user]);

  // La marca "actividades revisadas" la deja el Apoyo asignado o el Supervisor
  // (o ADMIN), con el acta enviada y aún sin aprobar. Es UNA por revisor y por
  // mes sobre el componente de Actividades (no por actividad) y significa
  // "revisé todas las actividades y no devolví ninguna". Es prerrequisito para
  // aprobar la cuenta de cobro. Bloqueada con el mes en corrección.
  const canMarkReview = (
    isSupportReviewer
    || user.roles.includes('ADMIN')
    || (isEffectiveSupervisor && user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.APPROVE))
  ) && approved === false && !inCorrection;

  const hasReturnedActivities = activities.some(
    (act) => getActivityStatus(act) === REVIEW_STATUS.RETURNED
  );

  // Actividades agregadas en el ciclo sin descripción: bloquean la
  // notificación de corrección (el correo detalla título y descripción).
  const addedWithoutDescription = hasAddedActivitiesWithoutDescription(activities, monthPayment);

  // Gate de envío a revisión: al menos una actividad con descripción y la
  // validación de SS según el mes — primer periodo ejecutable: certificados
  // de afiliación del contrato (EPS/AFP/ARL), no hay periodo anterior dentro
  // del contrato; periodos siguientes: SS del PERIODO EJECUTABLE ANTERIOR
  // diligenciada (pago a mes vencido; `payment_N` contiene la SS del periodo
  // N, la misma key que referencia el icono de la fila y que exige el gating
  // de aprobación). Los meses que una suspensión dejó en cero se saltan.
  // `submitBlockReason` alimenta el tooltip del botón.
  const submitIdx = payment ? parseInt(payment.split('_')[1], 10) : null;
  const submitPrevIdx = submitIdx !== null
    ? getPreviousExecutableIdx(getContractField(contract.news || [], contract)('paymentPlan'), submitIdx)
    : -1;
  const baseSubmitBlockReason = getSubmitForReviewBlockReason({
    activities,
    socialSecurityPayment: submitPrevIdx >= 0 ? contract.payments?.[`payment_${submitPrevIdx}`] : null,
    isFirstMonth: submitIdx !== null && submitPrevIdx < 0,
    contractCertificates: contract.certificates,
  });
  // Fecha de corte (payment-monthly-cutoff): pasado el corte del mes para la
  // naturaleza del contrato, el envío queda deshabilitado con ese motivo (ADMIN
  // exento). El backend lo revalida en UPDATE_CONTRACT.
  const paymentCutoff = usePaymentCutoff();
  const cutoffReason = paymentCutoff.blockReason(contract.nature);
  const submitBlockReason = cutoffReason || baseSubmitBlockReason;
  const canSubmitForReview = !submitBlockReason;

  // Revisión de los apoyos sobre el componente de Actividades del mes
  // (informativa: no bloquea al Supervisor).
  const activitiesReview = payment ? contract.payments?.[payment]?.activitiesReview : null;
  const myReview = activitiesReview?.reviews?.[user.userId];
  const supportName = (id) => {
    const support = (contract.supports || []).find((s) => s?.userId === id);
    return support ? `${support.name} ${support.lastName}` : id;
  };

  // Fechas del periodo desde la fuente única, sobre el plan VIGENTE: antes se
  // armaban con `contract.paymentPlan` y `contract.finishedAt` crudos, así que
  // un contrato prorrogado mostraba en el encabezado un periodo que no existía
  // en el plan con novedades aplicadas. Ya no se retrocede el fin cuando cae
  // fin de semana (los periodos se liquidan por días comerciales).
  const periodInfo = useMemo(() => {
    if (!payment) return null;
    const idx = parseInt(payment.split('_')[1], 10);
    const periodDays = getContractField(contract.news || [], contract)('paymentPlanDays');
    return getPeriodDateInfo(contract, periodDays, idx);
  }, [payment, contract]);

  const rangeDate = periodInfo?.range ?? null;

  const filteredTask = useMemo(() => {
    return tasks.filter((task) => !activities.map((act) => act.title).includes(task));
  }, [tasks, activities]);

  const addActivity = (value) => {
    const title = typeof value === 'string' ? value : activity;
    if (!title) return;

    setActivities((prev) => [...prev, {
      title,
      description: '',
      annexes: [],
      // En corrección la actividad queda marcada como agregada en el ciclo;
      // en borrador se conserva el esquema actual (sin `addedAt`).
      ...(inCorrection
        ? { reviewStatus: REVIEW_STATUS.PENDING, addedAt: new Date().toISOString() }
        : {}),
    }]);
  };

  const removeActivity = (idx) => {
    const acts = [...activities];
    acts.splice(idx, 1);
    setActivities(acts);
    // El índice de edición apunta a una posición del arreglo: tras el splice
    // señalaría a otra actividad, así que se cierra el detalle abierto.
    setEditing(undefined);
    // Con el acta ya enviada, eliminar cambia el conjunto revisado y obliga a
    // re-revisar, aunque no quede ninguna actividad marcada como corregida.
    if (inCorrection) setRemovedInCycle(true);
  };

  // En borrador se elimina directo (comportamiento existente). Con el acta ya
  // enviada —actividad devuelta o agregada en el ciclo— se pide confirmación:
  // el borrado es duro y se lleva consigo el historial de devoluciones.
  const requestRemoveActivity = (idx) => {
    if (approved === undefined) {
      removeActivity(idx);
      return;
    }
    setRemoveInfo({
      open: true,
      title: `¿Eliminar la actividad "${activities[idx]?.title}"?`,
      confirm: 'Eliminar',
      data: { idx },
    });
  };

  const updateActivity = (idx, newActivity) => {
    const acts = [...activities];
    const activity = acts[idx];
    acts[idx] = { ...activity, ...newActivity };

    // Marca local de edición del contratista (no se persiste). El cambio de
    // estado Devuelta → Pendiente ocurre solo al Guardar, no en cada
    // pulsación: si se hiciera aquí, la actividad dejaría de ser editable
    // en cuanto el usuario empieza a escribir.
    if (isContractor && (newActivity.description !== undefined || newActivity.annexes !== undefined)) {
      acts[idx]._dirty = true;
    }

    setActivities(acts);
  };

  // Prepara las actividades para persistir: retira la marca local _dirty y
  // retorna a Pendiente las actividades devueltas que el contratista editó,
  // conservando el historial de devoluciones. `correctedAt` identifica las
  // correcciones de cada ciclo (alimenta la notificación de corrección).
  const prepareActivitiesToSave = () => activities.map((act) => {
    const { _dirty, ...clean } = act;
    if (_dirty && getActivityStatus(clean) === REVIEW_STATUS.RETURNED) {
      return {
        ...clean,
        reviewStatus: REVIEW_STATUS.PENDING,
        rejected: undefined,
        correctedAt: new Date().toISOString(),
      };
    }
    return clean;
  });

  // La corrección de una actividad devuelta o la adición de una actividad en
  // el ciclo de corrección invalidan la revisión general sobre el componente
  // de Actividades: el conjunto revisado cambió y exige re-revisión. La
  // adición solo cuenta con el ciclo abierto: cerrado el ciclo, `startedAt`
  // persiste y un "Guardar" posterior no debe borrar marcas legítimas.
  // `removedInCycle` cubre la eliminación: al ser borrado duro no queda nada
  // en `activities` que delate el cambio del conjunto.
  const requiresReReview = () => removedInCycle || activities.some(
    (act) => (act._dirty && getActivityStatus(act) === REVIEW_STATUS.RETURNED)
      || (inCorrection && isActivityAddedInCycle(act, monthPayment))
  );

  // Marca (o deshace) la revisión del apoyo sobre el componente de Actividades
  // del mes. Solo afecta la entrada propia en `activitiesReview.reviews`; el
  // estado Revisada se deriva en lectura contra los supportIDs vigentes.
  const handleToggleActivitiesReview = async () => {
    if (!payment) return;
    setLoading(true);

    const review = { ...(contract.payments?.[payment]?.activitiesReview || {}) };
    const reviews = { ...(review.reviews || {}) };
    const isMarking = !reviews[user.userId];
    if (reviews[user.userId]) {
      delete reviews[user.userId];
    } else {
      reviews[user.userId] = {
        name: `${user.name} ${user.lastName}`,
        at: new Date().toISOString(),
      };
    }
    review.reviews = reviews;

    const payments = { ...(contract.payments || {}) };
    payments[payment] = { ...(payments[payment] || {}), activitiesReview: review };

    // Al MARCAR (no al deshacer), avisa al Supervisor efectivo. No se envía si
    // quien marca es el propio Supervisor (evita autonotificación). El correo
    // viaja en el mismo updateContract; una falla del correo no bloquea la marca.
    const activeSupervisor = getContractField(contract.news || [], contract)('supervisors')[0];
    const email = (isMarking && activeSupervisor?.email && activeSupervisor.userId !== user.userId)
      ? {
        emailTo: activeSupervisor.email,
        template: 'componentReviewedSupervisorNotice',
        data: {
          contractCode: contract.contractCode,
          usertitle: activeSupervisor.usertitle,
          fullname: `${activeSupervisor.name} ${activeSupervisor.lastName}`,
          position: activeSupervisor.position,
          entity: activeSupervisor.entity,
          dependence: activeSupervisor.dependence,
          componentLabel: 'las actividades',
          reviewedBy: `${user.name} ${user.lastName}`,
          rangeDate,
        },
      }
      : undefined;

    await updateContract(contract.contractCode, {
      [`payments.${payment}.activitiesReview`]: review,
      email,
    }, user);

    setContract({ ...contract, payments });
    setLoading(false);
  };

  // Devolución individual de una actividad (con observación obligatoria) o su
  // reversa (`data.undo`): la actividad vuelve a Pendiente y la devolución
  // vigente queda marcada como deshecha en el historial (no se borra).
  const handleActivityReturn = async (result, data) => {
    if (!result) {
      setReturnInfo({ open: false });
      setReturnNote('');
      return;
    }

    const note = returnNote.trim();
    if (!data?.undo && !note) {
      setInfoSnack({ open: true, message: 'La observación es requerida para devolver la actividad', severity: 'warning' });
      return;
    }

    setReturnInfo({ open: false });
    setReturnNote('');
    setLoading(true);

    const at = new Date().toISOString();
    const name = `${user.name} ${user.lastName}`;

    let acts;
    if (data?.undo) {
      // Deshacer: marca la devolución vigente (última sin `undone`) y retorna
      // la actividad a Pendiente. Compatible con el esquema viejo (`rejected`
      // sin `returns[]`): solo limpia el flag. Solo disponible antes de
      // notificar (el mes no está "En corrección").
      acts = activities.map((act, i) => {
        if (i !== data.idx) return act;
        const returns = [...(act.returns || [])];
        for (let r = returns.length - 1; r >= 0; r -= 1) {
          if (!returns[r].undone) {
            returns[r] = { ...returns[r], undone: { userId: user.userId, name, at } };
            break;
          }
        }
        return { ...act, reviewStatus: REVIEW_STATUS.PENDING, rejected: undefined, returns };
      });
    } else {
      // Identifica al autor de la devolución en el historial. El `snapshot`
      // conserva el contenido devuelto para contrastar la corrección posterior.
      const role = canReturnAsSupport ? 'support' : 'supervisor';
      acts = activities.map((act, i) => (i === data.idx ? {
        ...act,
        reviewStatus: REVIEW_STATUS.RETURNED,
        rejected: undefined,
        returns: [...(act.returns || []), {
          userId: user.userId,
          name,
          at,
          note,
          role,
          snapshot: {
            description: act.description || '',
            annexes: (act.annexes || []).map((a) => ({ id: a.id, name: a.name, url: a.url })),
          },
        }],
      } : act));
    }

    // La devolución de actividad NO envía correo por sí sola: el revisor
    // notifica en bloque con el botón "Enviar notificación de devoluciones"
    // (handleNotifyReturnedActivities). El deshacer tampoco notifica.
    const payments = { ...(contract.payments || {}) };
    payments[payment] = { ...(payments[payment] || {}), activities: acts };

    await updateContract(contract.contractCode, {
      [`payments.${payment}.activities`]: acts,
    }, user);

    setContract({ ...contract, payments });
    setActivities(acts);
    setLoading(false);
  };

  // Notifica en bloque al contratista TODAS las actividades devueltas vigentes
  // del mes (snapshot completo, no incremental) con su última observación, y
  // marca el mes "En corrección" (la pelota pasa al contratista; la revisión
  // queda bloqueada hasta que él notifique la corrección). Registra la fecha
  // del último envío en `activitiesReview.notifiedAt`, que se sobrescribe en
  // cada reenvío. No hay historial de lotes ni de ciclos.
  const handleNotifyReturnedActivities = async () => {
    if (!payment) return;

    const returnedActs = activities.filter(
      (act) => getActivityStatus(act) === REVIEW_STATUS.RETURNED
    );
    if (returnedActs.length === 0) return;

    if (!contract.contractor?.email) {
      setInfoSnack({ open: true, message: 'El contratista no tiene correo registrado', severity: 'warning' });
      return;
    }

    setLoading(true);
    const at = new Date().toISOString();

    // Última observación vigente por actividad devuelta.
    const items = returnedActs.map((act) => {
      const lastReturn = (act.returns || []).filter((r) => !r.undone).slice(-1)[0];
      return { title: act.title, note: lastReturn?.note || '' };
    });

    const email = {
      emailTo: contract.contractor.email,
      template: 'chargeAccountReturnedActivities',
      data: {
        contractCode: contract.contractCode,
        usertitle: contract.contractor.usertitle,
        fullname: `${contract.contractor.name} ${contract.contractor.lastName}`,
        position: contract.contractor.position,
        entity: contract.contractor.entity,
        dependence: contract.contractor.dependence,
        returnedBy: `${user.name} ${user.lastName}`,
        rangeDate,
        items,
      },
    };

    const correction = {
      status: CORRECTION_STATUS.IN_CORRECTION,
      startedAt: at,
      startedBy: { userId: user.userId, name: `${user.name} ${user.lastName}` },
    };
    const review = {
      ...(contract.payments?.[payment]?.activitiesReview || {}),
      notifiedAt: at,
      correction,
    };
    const payments = { ...(contract.payments || {}) };
    payments[payment] = { ...(payments[payment] || {}), activitiesReview: review };

    await updateContract(contract.contractCode, {
      [`payments.${payment}.activitiesReview.notifiedAt`]: at,
      [`payments.${payment}.activitiesReview.correction`]: correction,
      email,
    }, user);

    setContract({ ...contract, payments });
    setLoading(false);
    setInfoSnack({ open: true, message: 'Notificación de devoluciones enviada al contratista', severity: 'success' });
  };

  // Notificación de corrección del contratista: cierra el ciclo "En corrección"
  // y avisa al Supervisor efectivo y a los Apoyos asignados. El correo detalla
  // SOLO las correcciones del ciclo vigente (correctedAt >= startedAt), cada
  // una con el motivo de la devolución, el contenido devuelto (snapshot del
  // historial) y el corregido.
  const handleNotifyCorrection = async () => {
    if (!payment) return;

    const correction = contract.payments?.[payment]?.activitiesReview?.correction;
    const startedAt = correction?.startedAt;
    const at = new Date().toISOString();

    const correctedActs = activities.filter(
      (act) => act.correctedAt && (!startedAt || act.correctedAt >= startedAt)
    );
    const items = correctedActs.map((act) => {
      const lastReturn = (act.returns || []).filter((r) => !r.undone).slice(-1)[0];
      return {
        title: act.title,
        note: lastReturn?.note || '',
        before: lastReturn?.snapshot?.description || '',
        after: act.description || '',
      };
    });

    // Actividades agregadas por el contratista en el ciclo vigente: viajan en
    // una sección aparte del correo (título y descripción).
    const addedItems = activities
      .filter((act) => isActivityAddedInCycle(act, monthPayment))
      .map((act) => ({ title: act.title, description: act.description || '' }));

    const activeSupervisor = getContractField(contract.news || [], contract)('supervisors')[0];
    const recipients = [
      activeSupervisor,
      ...(contract.supports || []).filter((s) => supportIDs.includes(s?.userId)),
    ].filter((r) => r?.email);

    const contractorName = `${contract.contractor.name} ${contract.contractor.lastName}`;
    const email = recipients.map((recipient) => ({
      emailTo: recipient.email,
      template: 'chargeAccountCorrectionNotice',
      data: {
        contractCode: contract.contractCode,
        usertitle: recipient.usertitle,
        fullname: `${recipient.name} ${recipient.lastName}`,
        position: recipient.position,
        entity: recipient.entity,
        dependence: recipient.dependence,
        contractorName,
        rangeDate,
        items,
        addedItems,
      },
    }));

    setLoading(true);

    // La notificación persiste lo que informa: las actividades (agregadas y
    // correcciones sin guardar) viajan en el mismo write que cierra el ciclo,
    // para que el correo nunca detalle actividades inexistentes en Firestore.
    // Si el conjunto cambió (corrección o adición), se invalida la marca de
    // revisión para que la aprobación exija re-revisar.
    const acts = prepareActivitiesToSave();
    const invalidateReview = requiresReReview();

    const endedCorrection = {
      ...(correction || {}),
      status: CORRECTION_STATUS.CORRECTED,
      endedAt: at,
      endedBy: { userId: user.userId, name: contractorName },
    };
    const review = {
      ...(contract.payments?.[payment]?.activitiesReview || {}),
      correction: endedCorrection,
      ...(invalidateReview ? { reviews: {} } : {}),
    };
    const payments = { ...(contract.payments || {}) };
    payments[payment] = { ...(payments[payment] || {}), activities: acts, activitiesReview: review };

    await updateContract(contract.contractCode, {
      [`payments.${payment}.activities`]: acts,
      [`payments.${payment}.activitiesReview.correction`]: endedCorrection,
      ...(invalidateReview ? { [`payments.${payment}.activitiesReview.reviews`]: {} } : {}),
      email: email.length > 0 ? email : undefined,
    }, user);

    setContract({ ...contract, payments });
    setActivities(acts);
    setRemovedInCycle(false);
    setLoading(false);
    setInfoSnack({ open: true, message: 'Notificación de corrección enviada a la supervisión', severity: 'success' });
  };

  const handleCloseAccount = async () => {
    setLoading(true);

    // Copia del periodo, no mutación: si el backend rechaza el envío, el
    // contrato en memoria debe quedar como estaba.
    const payments = { ...(contract.payments || {}) };
    payments[payment] = {
      ...(payments[payment] || {}),
      activities: prepareActivitiesToSave(),
      approved: false,
      sentAt: new Date().toISOString(),
    };

    const activeSupervisor = getContractField(contract.news || [], contract)('supervisors')[0];
    // Notifica el envío a revisión al Supervisor efectivo y a todos los Apoyos a
    // la Supervisión asignados con correo. `email` como arreglo hace fan-out en
    // handleContract.
    const contractorName = `${contract.contractor.name} ${contract.contractor.lastName}`;
    const recipients = [
      activeSupervisor,
      ...(contract.supports || []).filter((s) => supportIDs.includes(s?.userId)),
    ].filter((r) => r?.email);

    const email = recipients.map((recipient) => ({
      emailTo: recipient.email,
      template: 'chargeAccountSupervisorCheck',
      data: {
        contractCode: contract.contractCode,
        usertitle: recipient.usertitle,
        fullname: `${recipient.name} ${recipient.lastName}`,
        position: recipient.position,
        entity: recipient.entity,
        dependence: recipient.dependence,
        contractorName,
        rangeDate,
      },
    }));

    const result = await updateContractResult(contract.contractCode, { payments, email }, user);
    setLoading(false);
    if (!result.ok) {
      // El backend es la autoridad (p. ej. 409 por fecha de corte): el acta
      // sigue como estaba y el motivo va al snack.
      setInfoSnack({
        open: true,
        severity: 'error',
        duration: 6000,
        message: result.message || 'No fue posible enviar el acta para aprobación',
      });
      return;
    }
    setContract({ ...contract, payments });
    onClose();
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();

    let message;
    activities.forEach((activity) => {
      if (!activity.description) message = 'Hay actividades sin descripción. Revisalas e ingresa una descripción';
    });

    if (message) {
      setInfoSnack({ open: true, message, severity: 'warning' });
      return;
    }

    setLoading(true);

    const invalidateReview = requiresReReview();
    const acts = prepareActivitiesToSave();
    let payments = {};
    if (contract.payments) payments = {...contract.payments};
    if (!payments[payment]) payments[payment] = {};
    payments[payment].activities = acts;
    if (invalidateReview) {
      payments[payment].activitiesReview = {
        ...(payments[payment].activitiesReview || {}),
        reviews: {},
      };
    }

    await updateContract(contract.contractCode, { payments }, user);
    setContract({ ...contract, payments });
    setActivities(acts);
    setLoading(false);
  };

  useEffect(() => {
    setRemovedInCycle(false);
    if (payment) {
      let pay = {};
      if (contract.payments && contract.payments[payment]) {
        pay = contract.payments[payment];
      }

      setActivities(pay.activities || []);
      setApproved(pay.approved);
    } else {
      setActivities([]);
      setApproved(undefined);
    }
  }, [payment]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    const id = requestAnimationFrame(updateScrollHint);
    window.addEventListener('resize', updateScrollHint);
    let observer;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateScrollHint);
      observer.observe(el);
      Array.from(el.children).forEach((child) => observer.observe(child));
    }
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', updateScrollHint);
      if (observer) observer.disconnect();
    };
  }, [open, activities, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <MySnackbar
        info={infoSnack}
        handleClose={() => setInfoSnack({ open: false })}
      />

      <DialogSignConfirmation
        confirmInfo={confirmInfo}
        handleCloseDialog={(result) => {
          if (result) handleCloseAccount();
          setConfirmInfo({ open: false });
        }}
      />

      {/* Eliminación de una actividad ya enviada (devuelta o agregada en el
          ciclo). El borrado es duro: no queda registro de la actividad ni de
          las devoluciones que acumulaba. */}
      <DialogConfirmation
        confirmInfo={removeInfo}
        handleCloseDialog={(result, data) => {
          if (result && data) removeActivity(data.idx);
          setRemoveInfo({ open: false });
        }}
        content={(
          <Typography variant="body2" sx={{ mt: 1, minWidth: 360 }}>
            La actividad se eliminará junto con su historial de devoluciones y no
            podrá recuperarse. Recuerda <strong>guardar</strong> para que el cambio
            quede registrado.
          </Typography>
        )}
      />

      <DialogConfirmation
        confirmInfo={returnInfo}
        handleCloseDialog={handleActivityReturn}
        content={returnInfo.data?.undo
          ? (
            <Typography variant="body2" sx={{ mt: 1, minWidth: 360 }}>
              La actividad volverá al estado <strong>Pendiente</strong>. La devolución
              no se elimina del historial: quedará marcada como deshecha con tu nombre
              y la fecha.
            </Typography>
          )
          : (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              label="Observación (requerida)"
              placeholder="Describa el motivo de la devolución de esta actividad"
              value={returnNote}
              onChange={(evt) => setReturnNote(evt.target.value)}
              sx={{ mt: 1, minWidth: 360 }}
            />
          )
        }
      />

      <Modal
        open={open}
        onClose={onClose}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={modalStyle}
          ref={modalRef}
          onScroll={updateScrollHint}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 'bold' }}>Actividades y anexos</Typography>
            <CloseIcon sx={{ cursor: 'pointer' }} onClick={onClose} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            En esta opción puedes registrar las actividades realizadas en el ciclo de pago. Por favor lista las actividades ejecutadas de acuerdo con las <strong>obligaciones de tu contrato</strong> y adjunta los anexos que soporten cada actividad.
          </Typography>

          {periodInfo && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="overline" color="text.secondary">
                Actividades del mes
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                {periodInfo.month}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Período: {periodInfo.range}
              </Typography>
            </Box>
          )}

          {/* Indicación del ciclo "En corrección": el contratista debe corregir
              y notificar; la revisión queda en espera (acciones bloqueadas). */}
          {inCorrection && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {isContractor
                ? 'El mes está en corrección: corrige las actividades devueltas, agrega las que falten, guarda y envía la notificación de corrección a la supervisión.'
                : 'El mes está en corrección por el contratista: las acciones de revisión se habilitarán cuando notifique su corrección.'}
              {activitiesReview?.correction?.startedAt
                ? ` (Devoluciones notificadas el ${moment(activitiesReview.correction.startedAt).format('YYYY-MM-DD HH:mm')}.)`
                : ''}
            </Alert>
          )}

          {canAddActivities
            && <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, my: 2, p: 2, backgroundColor: "#efefef", borderRadius: 2 }}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  value={activity || null}
                  options={filteredTask.filter(t => t.followup).map((t, tIdx) => `${tIdx + 1}. ${t.title}`)}
                  getOptionLabel={(option) => option}
                  onChange={(__, newValue) => {
                    setActivity(newValue);
                    if (newValue) addActivity(newValue);
                  }}
                  renderOption={(props, option) => {
                    const match = option.match(/^(\d+)\.\s*(.*)$/);
                    const num = match ? match[1] : '';
                    const text = match ? match[2] : option;
                    return (
                      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
                        <Box sx={{
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRadius: '50%',
                          minWidth: 26,
                          height: 26,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          mt: 0.25,
                          flexShrink: 0
                        }}>
                          {num}
                        </Box>
                        <Typography sx={{ whiteSpace: 'normal', wordBreak: 'break-word', flex: 1 }}>
                          {text}
                        </Typography>
                      </Box>
                    );
                  }}
                  noOptionsText="No hay más obligaciones por agregar"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecciona una obligación y agrégala"
                      placeholder="Busca por palabra clave"
                    />
                  )}
                />
              </FormControl>
              <Tooltip title={activity ? "Agregar otra vez esta obligación" : "Selecciona una obligación primero"}>
                <span>
                  <IconButton
                    sx={{ width: "56px", height: "56px" }}
                    onClick={() => addActivity()}
                    disabled={!activity}
                    color="primary"
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          }
          <Box sx={{ mb: 2 }}>
            {activities.map((activity, idx) => {
              const isAddedInCycle = isActivityAddedInCycle(activity, monthPayment);
              // Editable por el contratista en borrador; con el acta enviada,
              // solo la actividad devuelta (para corregirla) o la agregada en
              // el ciclo de corrección mientras este siga abierto.
              const readOnly = !isContractor || (
                approved !== undefined
                && !(approved === false && (
                  getActivityStatus(activity) === REVIEW_STATUS.RETURNED
                  || (inCorrection && isAddedInCycle)
                ))
              );
              // Eliminar en borrador y, con el mes "En corrección", sobre lo
              // que el contratista tiene en sus manos: la actividad devuelta
              // (puede corregirla o descartarla) y la agregada en el ciclo.
              // Fuera del ciclo una actividad enviada se corrige, no se borra:
              // el revisor aún está armando su notificación de devoluciones.
              const canRemove = !readOnly && (
                approved === undefined
                || (inCorrection && (
                  isAddedInCycle
                  || getActivityStatus(activity) === REVIEW_STATUS.RETURNED
                ))
              );

              return (
                <ChargeAccountActivity
                  idx={idx}
                  key={idx}
                  {...activity}
                  contractCode={contract.contractCode}
                  updateActivity={updateActivity}
                  removeActivity={requestRemoveActivity}
                  editing={editing === idx}
                  readOnly={readOnly}
                  canRemove={canRemove}
                  isAddedInCycle={isAddedInCycle}
                  setEditing={setEditing}
                  payment={monthPayment}
                  setInfoSnack={setInfoSnack}
                  canReturn={canReturn && !inCorrection}
                  onReturnActivity={() => setReturnInfo({
                    open: true,
                    title: `Devolver actividad — ${activity.title}`,
                    confirm: 'Devolver',
                    data: { idx },
                  })}
                  onUndoReturn={() => setReturnInfo({
                    open: true,
                    title: `Deshacer devolución — ${activity.title}`,
                    confirm: 'Deshacer',
                    data: { idx, undo: true },
                  })}
                />
              );
            })}
          </Box>

          {/* Revisión del componente de Actividades: marca del Apoyo asignado
              o del Supervisor ("revisé todas las actividades y no devolví
              ninguna"). Prerrequisito para aprobar la cuenta de cobro. */}
          {!isContractor && approved === false && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Revisión de actividades
              </Typography>
              {/* Solo quienes YA marcaron (apoyo o supervisor); basta con que
                  al menos uno lo haga y los pendientes no se listan. */}
              {Object.keys(activitiesReview?.reviews || {}).length === 0 && (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }} color="text.secondary">
                  Aún sin marcas de revisión
                </Typography>
              )}
              {Object.entries(activitiesReview?.reviews || {}).map(([id, mark]) => (
                <Typography key={id} variant="body2">
                  ✓ {mark?.name || supportName(id)}
                  {mark?.at
                    ? ` — ${moment(mark.at).format('YYYY-MM-DD HH:mm')}`
                    : ''}
                </Typography>
              ))}
              {canMarkReview && (
                <Tooltip
                  title={hasReturnedActivities
                    ? 'Hay actividades devueltas pendientes de corrección: la marca de revisión significa que no devolviste ninguna'
                    : ''}
                >
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="info"
                      sx={{ mt: 1.5 }}
                      disabled={loading || hasReturnedActivities}
                      startIcon={myReview ? <UndoIcon /> : <GradingIcon />}
                      onClick={handleToggleActivitiesReview}
                    >
                      {myReview
                        ? 'Deshacer mi revisión'
                        : 'Marcar actividades como revisadas'}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 5 }}
            disabled={loading}
          >
            {loading && <CircularProgress size={26} sx={{ mr: 1 }} />} Guardar
          </Button>
          {approved === undefined && isContractor
            && !activities.some((act) => getActivityStatus(act) === REVIEW_STATUS.RETURNED)
            && <Tooltip title={submitBlockReason}>
              <span>
                <Button
                  variant="outlined"
                  sx={{ mt: 5, ml: 3 }}
                  disabled={loading || !canSubmitForReview}
                  onClick={() => setConfirmInfo({
                    open: true,
                    title: '¿Realmente deseas enviar a aprobación?'
                  })}
                >
                  {loading && <CircularProgress size={26} sx={{ mr: 1 }} />} Enviar para aprobación
                </Button>
              </span>
            </Tooltip>
          }
          {/* Notificación de corrección del contratista: cierra el ciclo "En
              corrección" y avisa a la supervisión. Habilitada solo cuando ya
              no quedan actividades devueltas (todas corregidas y guardadas). */}
          {isContractor && approved === false && inCorrection && (
            <Tooltip title={activities.length === 0
              ? 'El mes quedó sin actividades: registra al menos una antes de notificar'
              : hasReturnedActivities
                ? 'Corrige y guarda todas las actividades devueltas antes de notificar'
                : addedWithoutDescription
                  ? 'Completa la descripción de las actividades agregadas antes de notificar'
                  : ''}
            >
              <span>
                <Button
                  variant="contained"
                  color="error"
                  sx={{ mt: 5, ml: 3 }}
                  disabled={loading || activities.length === 0 || hasReturnedActivities || addedWithoutDescription}
                  onClick={handleNotifyCorrection}
                >
                  {loading && <CircularProgress size={26} sx={{ mr: 1 }} />} Enviar notificación de corrección
                </Button>
              </span>
            </Tooltip>
          )}
          {/* Notificación en bloque de las devoluciones de actividades: solo
              para quien puede devolver, habilitada con al menos una actividad
              devuelta. Guardar devoluciones no notifica; este botón sí y
              además marca el mes "En corrección". */}
          {canReturn && (
            <>
              <Tooltip title={inCorrection
                ? 'El mes está en corrección por el contratista'
                : hasReturnedActivities ? '' : 'No hay actividades devueltas para notificar'}
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{ mt: 5, ml: 3 }}
                    disabled={loading || !hasReturnedActivities || inCorrection}
                    onClick={handleNotifyReturnedActivities}
                  >
                    {loading && <CircularProgress size={26} sx={{ mr: 1 }} />} Enviar notificación de devoluciones
                  </Button>
                </span>
              </Tooltip>
              {activitiesReview?.notifiedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Última notificación de devoluciones enviada el {moment(activitiesReview.notifiedAt).format('YYYY-MM-DD HH:mm')}
                </Typography>
              )}
            </>
          )}
          <Fade in={showScrollHint} unmountOnExit mountOnEnter>
            <Box
              sx={{
                position: 'sticky',
                bottom: 8,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
                mt: -3,
                zIndex: 1,
              }}
            >
              <Tooltip title="Hay más contenido abajo">
                <IconButton
                  size="small"
                  onClick={() => modalRef.current?.scrollTo({ top: modalRef.current.scrollHeight, behavior: 'smooth' })}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: 3,
                    pointerEvents: 'auto',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        </Box>
      </Modal>
    </>
  );
};

export default ChargeAccountModal;
